import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { FaArrowLeft } from 'react-icons/fa';
import { HiUsers } from 'react-icons/hi';
import CommunitySelector from '../../../../components/headers/CommunitySelector';
import useCommunities from '../../../../hooks/community/useCommunities';
import useSelectedCommunity from '../../../../hooks/community/useSelectedCommunity';
import { setSelectedCommunity } from '../../../../redux/slices/applicationSlice';
import Community, { CommunityRole } from '../../../../types/community';
import CreateCommunityModal from '../../../../components/modals/CreateCommunityModal';
import { RootState } from '../../../../redux/reducer';

type TabId = 'overview' | 'events' | 'finances' | 'tasks' | 'scores' | 'albums' | 'forms';

export default function CommunityLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const communities = useCommunities();
  const selectedCommunity = useSelectedCommunity();
  const selectedCommunityId = useSelector((state: RootState) => state.application.selectedCommunity);

  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Determine active tab from URL
  const getActiveTab = (): TabId => {
    if (location.pathname === '/community') return 'overview';
    if (location.pathname.includes('/events')) return 'events';
    if (location.pathname.includes('/albums')) return 'albums';
    if (location.pathname.includes('/tasks')) return 'tasks';
    if (location.pathname.includes('/scores')) return 'scores';
    if (location.pathname.includes('/finances')) return 'finances';
    if (location.pathname.includes('/forms')) return 'forms';
    return 'overview';
  };

  const activeTab = getActiveTab();

  const getTabs = () => {
    const tabs = [{ id: 'overview' as TabId, label: t('page.community.tabs.overview') }];

    if (selectedCommunity?.features?.EVENTS) {
      tabs.push({ id: 'events' as TabId, label: t('page.community.tabs.events') });
    }
    if (selectedCommunity?.features?.PHOTO_ALBUMS) {
      tabs.push({ id: 'albums' as TabId, label: t('page.community.tabs.albums') });
    }
    if (selectedCommunity?.features?.TASKS) {
      tabs.push({ id: 'tasks' as TabId, label: t('page.community.tabs.tasks') });
    }
    if (selectedCommunity?.features?.STATS_AND_SCORES) {
      tabs.push({ id: 'scores' as TabId, label: t('page.community.tabs.scores') });
    }
    if (selectedCommunity?.features?.COST_SPLITTING) {
      tabs.push({ id: 'finances' as TabId, label: t('page.community.tabs.finances') });
    }
    if (selectedCommunity?.features?.FORMS) {
      tabs.push({ id: 'forms' as TabId, label: t('page.community.tabs.forms') });
    }

    return tabs;
  };

  useEffect(() => {
    if (communities !== undefined) {
      setIsLoading(false);
    }
  }, [communities]);

  // If a navigation state includes a communityId, ensure we select it immediately so subsequent redirect logic doesn't kick in
  useEffect(() => {
    const stateCommunityId = (location.state as any)?.communityId;
    if (stateCommunityId && stateCommunityId !== selectedCommunityId) {
      try {
        dispatch(setSelectedCommunity(Number(stateCommunityId)));
      } catch (e) {
        /* ignore */
      }
    }
  }, [location.state, selectedCommunityId, dispatch]);

  useEffect(() => {
    // Redirect to communities list if no community is selected
    if (communities && communities.length > 0 && !selectedCommunityId) {
      navigate('/communities');
    }
  }, [selectedCommunityId, communities, navigate]);

  const handleSelectCommunity = (community: Community) => {
    dispatch(setSelectedCommunity(community.id));
    navigate('/community');
  };

  const handleBack = () => {
    navigate('/communities');
  };

  const handleCreateNew = () => {
    setShowCreateModal(true);
  };

  const handleTabChange = (tabId: string) => {
    if (!selectedCommunityId) return;

    switch (tabId) {
      case 'overview':
        navigate('/community');
        break;
      case 'events':
        navigate('/community/events');
        break;
      case 'albums':
        navigate('/community/albums');
        break;
      case 'tasks':
        navigate('/community/tasks');
        break;
      case 'scores':
        navigate('/community/scores');
        break;
      case 'finances':
        navigate('/community/finances');
        break;
      case 'forms':
        navigate('/community/forms');
        break;
      default:
        navigate('/community');
    }
  };

  // Map communities into CommunityData for selector
  const communityDataList =
    communities?.map((c) => ({
      id: c.id,
      businessId: 0,
      name: c.name,
      picture: c.picture || '',
      memberCount: 0,
      description: c.description,
      link: '',
      role: c.role as any,
      type: c.type as any,
      features: c.features,
      memberPermissions: c.memberPermissions,
    })) || [];

  const selectedCommunityData = selectedCommunity
    ? {
        id: selectedCommunity.id,
        businessId: 0,
        name: selectedCommunity.name,
        picture: selectedCommunity.picture || '',
        memberCount: 0,
        description: selectedCommunity.description,
        link: '',
        role: selectedCommunity.role as any,
        type: selectedCommunity.type as any,
        features: selectedCommunity.features,
        memberPermissions: selectedCommunity.memberPermissions,
      }
    : null;

  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8">
        <p className="text-secondary-600">{t('page.dashboard.community.loading')}</p>
      </div>
    );
  }

  if (!communities || communities.length === 0) {
    return (
      <div className="flex min-h-[36vh] flex-col items-center justify-center gap-3 p-6">
        <HiUsers className="h-8 w-8 text-primary" />
        <h2 className="text-xl font-semibold text-gray-900">{t('page.dashboard.communities.noCommunities')}</h2>
        <p className="text-center text-sm text-gray-600">{t('page.dashboard.communities.noCommunitiesDescription')}</p>
        <button
          type="button"
          onClick={handleCreateNew}
          className="rounded-md bg-primary px-4 py-2 text-sm text-white transition-colors hover:bg-opacity-90">
          {t('page.communityOverview.new')}
        </button>

        <CreateCommunityModal
          open={showCreateModal}
          setOpen={setShowCreateModal}
          onSuccess={() => {
            setShowCreateModal(false);
            navigate('/community');
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      {/* Selector + Tabs (moved from header) */}
      <div className="border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex w-full flex-col gap-4">
            <div className="flex w-full flex-col justify-between gap-4 md:flex-row">
              <button
                type="button"
                onClick={handleBack}
                className="flex items-center gap-2 rounded text-details/60 hover:text-details/80">
                <FaArrowLeft className="h-4 w-4" />
                {t('general.communitiesOverview')}
              </button>
              {selectedCommunity?.role === CommunityRole.ADMIN && (
                <p className="flex flex-col text-sm text-details/70 sm:text-base md:items-end">
                  {t('page.dashboard.communities.managePrompt')}{' '}
                  <a
                    href="https://www.veracommunity.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cursor-pointer font-bold text-primary underline"
                    aria-label={t('page.dashboard.communities.manageAdmin')}>
                    {t('page.dashboard.communities.manageAdmin')}
                  </a>
                </p>
              )}
            </div>

            <div className="hidden w-full sm:flex">
              <CommunitySelector
                communities={communityDataList}
                selectedCommunity={selectedCommunityData}
                onSelectCommunity={(community) =>
                  handleSelectCommunity(communities?.find((c) => c.id === community.id)!)
                }
                onCreateNew={handleCreateNew}
              />
            </div>
          </div>

          <div />
        </div>
      </div>

      <div className="px-6">
        <nav className="border-secondary-100 -mx-6 flex flex-nowrap gap-6 overflow-x-auto border-b px-6 pb-2 sm:mx-0 sm:gap-12 sm:px-0">
          {getTabs().map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleTabChange(tab.id)}
              className={classNames('whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors', {
                'border-primary text-primary': activeTab === tab.id,
                'border-transparent text-details/60 hover:text-primary': activeTab !== tab.id,
              })}>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="mx-auto w-full flex-1 px-4 py-4 sm:px-8">
        <Outlet />
      </div>

      <CreateCommunityModal
        open={showCreateModal}
        setOpen={setShowCreateModal}
        onSuccess={() => {
          setShowCreateModal(false);
          navigate('/community');
        }}
      />
    </div>
  );
}
